import { useState, useRef, useCallback, useEffect } from 'react';
import { BlossomUploader } from '@nostrify/nostrify/uploaders';
import { nip19 } from 'nostr-tools';
import { useNostr } from '@nostrify/react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { useAdminConfig, useAdminAnalytics, canUpload } from '@/hooks/useAdminConfig';
import type { NostrEvent } from '@nostrify/nostrify';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/useToast';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { X, Plus, Upload, Check, AlertCircle, Server, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { YouTubeEmbed, YouTubeIcon } from '@/components/YouTubeEmbed';
import {
  extractYouTubeVideoId,
  isYouTubeUrl,
  getYouTubeThumbnailUrl,
  normaliseYouTubeUrl,
} from '@/lib/youtube';
import { mobileDrawerContentStyle, MOBILE_DRAWER_OVERLAY_CLASS } from '@/lib/drawerStyles';

/* ─── Capture a random frame from a video URL ────────────────── */
async function captureVideoFrame(videoUrl: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;

    video.addEventListener('loadedmetadata', () => {
      // Pick a random time between 5% and 85% to avoid black/credit frames
      const duration = video.duration;
      const seekTime = duration > 0 ? duration * (0.05 + Math.random() * 0.8) : 0;
      video.currentTime = seekTime;
    });

    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas 2D context unavailable')); return; }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          if (!blob) { reject(new Error('Failed to capture frame')); return; }
          const file = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
          resolve(file);
        }, 'image/jpeg', 0.92);
      } catch (err) {
        reject(err);
      }
    });

    video.addEventListener('error', () => reject(new Error('Could not load video for thumbnail generation')));
    video.src = videoUrl;
  });
}

/* ─── Blossom server presets ─────────────────────────────────────── */
const PRESET_SERVERS = [
  { url: 'https://blossom.primal.net/',   label: 'Primal',      badge: 'Recommended' },
  { url: 'https://blossom.nostr.build/',  label: 'nostr.build', badge: '' },
  { url: 'https://blossom.ditto.pub/',    label: 'Ditto',       badge: '' },
];

/* ─── Types ──────────────────────────────────────────────────────── */
interface UploadModalProps {
  open: boolean;
  onClose: () => void;
}

// Modal mode: upload a file to Blossom, or bridge a YouTube URL to Nostr
type ModalMode = 'upload' | 'youtube';

// Steps flow in order: file → details → servers → publishing
type Step = 'file' | 'details' | 'servers' | 'publishing';

interface UploadState {
  file: File | null;
  videoUrl: string;
  uploadProgress: number;
  uploadError: string;
  uploading: boolean;
}

interface ThumbState {
  file: File | null;
  url: string;
  uploading: boolean;
  error: string;
}

/* ─── Helpers ────────────────────────────────────────────────────── */
function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isValidUrl(s: string) {
  try { new URL(s); return true; } catch { return false; }
}

/* ─── Sub-components ─────────────────────────────────────────────── */
function StepIndicator({ step }: { step: Step }) {
  // Steps are displayed in the correct flow order: file → details → servers → publishing
  const steps: { key: Step; label: string }[] = [
    { key: 'file',       label: 'File' },
    { key: 'details',    label: 'Details' },
    { key: 'servers',    label: 'Servers' },
    { key: 'publishing', label: 'Publish' },
  ];
  const idx = steps.findIndex(s => s.key === step);
  return (
    <div className="flex items-center gap-1 mb-5">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center gap-1">
          <div
            className={cn(
              'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors',
              i < idx  ? 'text-white' : '',
              i === idx ? 'text-white' : '',
              i > idx  ? '' : '',
            )}
            style={{
              background: i < idx ? 'var(--gb-accent)' : i === idx ? 'var(--gb-accent)' : 'var(--bg-primary)',
              border: i > idx ? '1px solid var(--gb-border)' : 'none',
              color: i > idx ? 'var(--text-secondary)' : '#fff',
            }}
          >
            {i < idx ? <Check size={12} /> : i + 1}
          </div>
          <span className="text-xs hidden sm:inline" style={{ color: i === idx ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <div className="w-4 h-px mx-1" style={{ background: i < idx ? 'var(--gb-accent)' : 'var(--gb-border)' }} />
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── File drop zone ─────────────────────────────────────────────── */
function FileDropZone({
  onFile,
  accept,
  label,
  hint,
  children,
}: {
  onFile: (f: File) => void;
  accept: string;
  label: string;
  hint: string;
  children?: React.ReactNode;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className="relative rounded-xl border-2 border-dashed cursor-pointer transition-all flex flex-col items-center justify-center gap-3 py-8 px-6 text-center"
      style={{
        borderColor: dragging ? 'var(--gb-accent)' : 'var(--gb-border)',
        background: dragging ? 'rgba(58,158,58,0.06)' : 'var(--bg-primary)',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); }}
      />
      {children ?? (
        <>
          <div className="rounded-full p-3" style={{ background: 'rgba(58,158,58,0.12)' }}>
            <Upload size={24} style={{ color: 'var(--gb-accent)' }} />
          </div>
          <div>
            <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{label}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{hint}</p>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Blossom server manager ─────────────────────────────────────── */
function BlossomServerManager({
  selectedServers,
  onChange,
}: {
  selectedServers: string[];
  onChange: (servers: string[]) => void;
}) {
  const [customInput, setCustomInput] = useState('');
  const [customError, setCustomError] = useState('');

  const toggle = (url: string) => {
    if (selectedServers.includes(url)) {
      if (selectedServers.length === 1) return; // keep at least one
      onChange(selectedServers.filter(s => s !== url));
    } else {
      onChange([...selectedServers, url]);
    }
  };

  const addCustom = () => {
    const raw = customInput.trim().replace(/\/?$/, '/');
    if (!isValidUrl(raw)) { setCustomError('Enter a valid URL (https://…)'); return; }
    if (selectedServers.includes(raw)) { setCustomError('Already in the list'); return; }
    setCustomError('');
    setCustomInput('');
    onChange([...selectedServers, raw]);
  };

  const removeCustom = (url: string) => {
    if (selectedServers.length === 1) return;
    onChange(selectedServers.filter(s => s !== url));
  };

  const isPreset = (url: string) => PRESET_SERVERS.some(p => p.url === url);
  const customServers = selectedServers.filter(s => !isPreset(s));

  return (
    <div className="space-y-3">
      {/* Preset servers */}
      <div className="space-y-2">
        {PRESET_SERVERS.map(({ url, label, badge }) => {
          const active = selectedServers.includes(url);
          return (
            <button
              key={url}
              type="button"
              onClick={() => toggle(url)}
              className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all"
              style={{
                background: active ? 'rgba(58,158,58,0.1)' : 'var(--bg-primary)',
                border: `1px solid ${active ? 'var(--gb-accent)' : 'var(--gb-border)'}`,
              }}
            >
              <div
                className="w-5 h-5 rounded flex items-center justify-center shrink-0 transition-all"
                style={{ background: active ? 'var(--gb-accent)' : 'transparent', border: active ? 'none' : '1px solid var(--gb-border)' }}
              >
                {active && <Check size={12} color="#fff" />}
              </div>
              <Server size={15} style={{ color: active ? 'var(--gb-accent)' : 'var(--text-secondary)', flexShrink: 0 }} />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{label}</span>
                <span className="text-xs ml-2" style={{ color: 'var(--text-secondary)' }}>{url}</span>
              </div>
              {badge && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium shrink-0" style={{ background: 'rgba(58,158,58,0.15)', color: 'var(--gb-accent)' }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom servers */}
      {customServers.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Custom</p>
          {customServers.map(url => (
            <div
              key={url}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm"
              style={{ background: 'rgba(58,158,58,0.08)', border: '1px solid var(--gb-accent)' }}
            >
              <Server size={14} style={{ color: 'var(--gb-accent)', flexShrink: 0 }} />
              <span className="flex-1 min-w-0 truncate text-xs font-mono" style={{ color: 'var(--text-primary)' }}>{url}</span>
              <button
                type="button"
                onClick={() => removeCustom(url)}
                className="shrink-0 rounded p-0.5 transition-opacity hover:opacity-70"
                style={{ color: 'var(--text-secondary)' }}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add custom server */}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Add custom server</p>
        <div className="flex gap-2">
          <Input
            value={customInput}
            onChange={e => { setCustomInput(e.target.value); setCustomError(''); }}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustom())}
            placeholder="https://your-blossom-server.com/"
            className="flex-1 text-sm font-mono"
            style={{ background: 'var(--bg-primary)', border: `1px solid ${customError ? '#f44336' : 'var(--gb-border)'}`, color: 'var(--text-primary)' }}
          />
          <button
            type="button"
            onClick={addCustom}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white shrink-0 transition-opacity hover:opacity-90"
            style={{ background: 'var(--gb-accent)' }}
          >
            <Plus size={15} /> Add
          </button>
        </div>
        {customError && <p className="text-xs" style={{ color: '#f44336' }}>{customError}</p>}
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          Files are tried on each selected server in order until one succeeds.
        </p>
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */
export function UploadModal({ open, onClose }: UploadModalProps) {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const isMobile = useIsMobile();
  const { config, updateConfig } = useAppContext();
  const { config: adminConfig } = useAdminConfig();
  const { recordUpload } = useAdminAnalytics();
  const { mutate: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { toast } = useToast();

  /* modal mode: 'upload' (Blossom) vs 'youtube' (YouTube bridge) */
  const [mode, setMode] = useState<ModalMode>('upload');

  /* step state — flows: file → details → servers → publishing */
  const [step, setStep] = useState<Step>('file');

  /* publish outcome tracked separately so step 4 can show success/error */
  const [publishError, setPublishError] = useState('');
  const [publishDone, setPublishDone] = useState(false);

  /* YouTube bridge state */
  const [ytUrl, setYtUrl] = useState('');
  const [ytUrlError, setYtUrlError] = useState('');
  const [ytTitle, setYtTitle] = useState('');
  const [ytDescription, setYtDescription] = useState('');
  const [ytTagsInput, setYtTagsInput] = useState('');
  const [ytPublishing, setYtPublishing] = useState(false);
  const [ytPublishDone, setYtPublishDone] = useState(false);
  const [ytPublishError, setYtPublishError] = useState('');

  /* video file + upload */
  const [video, setVideo] = useState<UploadState>({
    file: null, videoUrl: '', uploadProgress: 0, uploadError: '', uploading: false,
  });

  /* thumbnail */
  const [thumb, setThumb] = useState<ThumbState>({
    file: null, url: '', uploading: false, error: '',
  });
  const [autoThumbGenerating, setAutoThumbGenerating] = useState(false);

  /* metadata */
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [duration, setDuration] = useState('');

  /* selected Blossom servers (local to this modal, seeded from app config) */
  const [selectedServers, setSelectedServers] = useState<string[]>(() => {
    const cfg = config.blossomServerMetadata.servers;
    const presets = PRESET_SERVERS.map(p => p.url);
    // ensure primal + nostr.build are always offered; then append user customs
    const base = [
      'https://blossom.primal.net/',
      'https://blossom.nostr.build/',
      ...cfg.filter(s => !presets.includes(s)),
    ];
    return [...new Set(base)];
  });

  const [showServersInline, setShowServersInline] = useState(false);

  /**
   * Stable `d` tag for the kind 34235 event — generated once per upload session
   * and reused on retries so that a failed-then-retried publish replaces the same
   * addressable event rather than creating a second one.
   */
  const eventDTagRef = useRef<string>('');

  /**
   * Re-entrancy guard: set to `true` the moment we call publishEvent and only
   * cleared on failure (or modal close). Prevents a double-click or React
   * StrictMode double-invoke from firing two publish calls.
   */
  const publishingRef = useRef(false);

  /* reset everything when modal closes */
  useEffect(() => {
    if (!open) {
      setMode('upload');
      setStep('file');
      setVideo({ file: null, videoUrl: '', uploadProgress: 0, uploadError: '', uploading: false });
      setThumb({ file: null, url: '', uploading: false, error: '' });
      setTitle(''); setDescription(''); setTagsInput(''); setDuration('');
      setShowServersInline(false);
      setAutoThumbGenerating(false);
      setPublishError('');
      setPublishDone(false);
      eventDTagRef.current = '';
      publishingRef.current = false;
      // YouTube bridge reset
      setYtUrl(''); setYtUrlError(''); setYtTitle(''); setYtDescription('');
      setYtTagsInput(''); setYtPublishing(false); setYtPublishDone(false); setYtPublishError('');
    }
  }, [open]);

  /* ── Upload a file to the selected Blossom servers ── */
  const uploadToBlossom = useCallback(async (
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<string> => {
    if (!user) throw new Error('Must be logged in');

    // Save server prefs back to app config
    updateConfig(prev => ({
      ...prev,
      blossomServerMetadata: {
        servers: selectedServers,
        updatedAt: Math.floor(Date.now() / 1000),
      },
    }));

    const uploader = new BlossomUploader({
      servers: selectedServers,
      signer: user.signer,
    });

    // Fake progressive progress since BlossomUploader doesn't stream progress
    let fake = 0;
    const interval = setInterval(() => {
      fake = Math.min(fake + 8, 85);
      onProgress?.(fake);
    }, 300);

    try {
      const tags = await uploader.upload(file);
      clearInterval(interval);
      onProgress?.(100);
      const url = tags.find(([k]) => k === 'url')?.[1];
      if (!url) throw new Error('Blossom server did not return a URL');
      return url;
    } catch (err) {
      clearInterval(interval);
      throw err;
    }
  }, [user, selectedServers, updateConfig]);

  /* ── Handle video file selected ── */
  const handleVideoFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('video/')) {
      toast({ title: 'Invalid file', description: 'Please select a video file.', variant: 'destructive' });
      return;
    }

    setVideo(v => ({ ...v, file, uploading: true, uploadError: '', uploadProgress: 0 }));

    try {
      const url = await uploadToBlossom(file, pct =>
        setVideo(v => ({ ...v, uploadProgress: pct })),
      );
      setVideo(v => ({ ...v, uploading: false, videoUrl: url }));
      // Mint the stable d-tag for this upload session (used by handlePublish)
      eventDTagRef.current = `goblinbox-${Date.now()}`;
      // Step 1 → Step 2 (details)
      setStep('details');

      // Auto-generate thumbnail silently in the background
      setAutoThumbGenerating(true);
      try {
        const frameFile = await captureVideoFrame(url);
        setThumb(t => ({ ...t, file: frameFile, uploading: true, error: '' }));
        const thumbUrl = await uploadToBlossom(frameFile);
        setThumb(t => ({ ...t, uploading: false, url: thumbUrl }));
      } catch {
        // Non-fatal — user can still add one manually
        setThumb(t => ({ ...t, uploading: false }));
      } finally {
        setAutoThumbGenerating(false);
      }
    } catch (err) {
      const msg = String(err);
      setVideo(v => ({ ...v, uploading: false, uploadError: msg }));
      toast({ title: 'Upload failed', description: msg, variant: 'destructive' });
    }
  }, [uploadToBlossom, toast]);

  /* ── Handle thumbnail file selected ── */
  const handleThumbFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please select an image.', variant: 'destructive' });
      return;
    }
    setThumb(t => ({ ...t, file, uploading: true, error: '' }));
    try {
      const url = await uploadToBlossom(file);
      setThumb(t => ({ ...t, uploading: false, url }));
    } catch (err) {
      const msg = String(err);
      setThumb(t => ({ ...t, uploading: false, error: msg }));
      toast({ title: 'Thumbnail upload failed', description: msg, variant: 'destructive' });
    }
  }, [uploadToBlossom, toast]);

  /* ── Auto-generate thumbnail from a random video frame ── */
  const handleAutoThumbnail = useCallback(async () => {
    if (!video.videoUrl) return;
    setAutoThumbGenerating(true);
    setThumb(t => ({ ...t, error: '' }));
    try {
      const frameFile = await captureVideoFrame(video.videoUrl);
      setThumb(t => ({ ...t, file: frameFile, uploading: true, error: '' }));
      const url = await uploadToBlossom(frameFile);
      setThumb(t => ({ ...t, uploading: false, url }));
    } catch (err) {
      const msg = String(err);
      setThumb(t => ({ ...t, uploading: false, error: msg }));
      toast({ title: 'Auto-thumbnail failed', description: msg, variant: 'destructive' });
    } finally {
      setAutoThumbGenerating(false);
    }
  }, [video.videoUrl, uploadToBlossom, toast]);

  /* ── YouTube bridge: publish YouTube video as a Nostr event ── */
  const handleYouTubePublish = useCallback(async () => {
    if (!user) return;

    const videoId = extractYouTubeVideoId(ytUrl.trim());
    if (!videoId) {
      setYtUrlError('Please enter a valid YouTube URL.');
      return;
    }
    if (!ytTitle.trim()) {
      toast({ title: 'Title required', description: 'Add a title before publishing.', variant: 'destructive' });
      return;
    }

    setYtPublishing(true);
    setYtPublishError('');
    setYtPublishDone(false);

    const canonicalUrl = normaliseYouTubeUrl(ytUrl.trim());
    const thumbUrl = getYouTubeThumbnailUrl(videoId);
    const dTag = `goblinbox-yt-${videoId}`;

    const parsedTags = ytTagsInput.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);

    const tags: string[][] = [
      ['d', dTag],
      ['title', ytTitle.trim()],
      ['published_at', String(Math.floor(Date.now() / 1000))],
      ['alt', ytTitle.trim()],
      ['r', canonicalUrl],
      // YouTube source marker
      ['source', 'youtube'],
      // imeta so clients know where the media lives
      ['imeta', `url ${canonicalUrl}`, `m video/youtube`, `image ${thumbUrl}`],
    ];

    parsedTags.forEach(t => tags.push(['t', t]));
    // Always add a 'youtube' tag so people can filter by it
    if (!parsedTags.includes('youtube')) tags.push(['t', 'youtube']);

    const footer = '\n\nhttps://goblinbox.net';
    const content = ytDescription.trim() ? ytDescription.trim() + footer : footer.trim();

    try {
      const videoEvent = await new Promise<NostrEvent>((resolve, reject) => {
        publishEvent(
          { kind: 34235, content, tags },
          {
            onSuccess: (ev: NostrEvent) => resolve(ev),
            onError: (err: unknown) => reject(err),
          },
        );
      });

      // Record upload analytics
      recordUpload({
        pubkey: user.pubkey,
        eventId: videoEvent.id,
        title: ytTitle.trim(),
        timestamp: Math.floor(Date.now() / 1000),
        tags: [...parsedTags, 'youtube'],
      });

      // Companion kind 1 note (fire-and-forget)
      try {
        const hashtagLine = parsedTags.map(t => `#${t}`).join(' ');
        const parts: string[] = [canonicalUrl, ytTitle.trim()];
        if (ytDescription.trim()) parts.push(ytDescription.trim());
        if (hashtagLine) parts.push(hashtagLine);
        parts.push('https://goblinbox.net');
        const kind1Content = parts.join('\n\n');

        const kind1Tags: string[][] = [
          ['r', canonicalUrl],
          ['e', videoEvent.id, '', 'mention'],
        ];
        parsedTags.forEach(t => kind1Tags.push(['t', t]));
        if (!parsedTags.includes('youtube')) kind1Tags.push(['t', 'youtube']);

        const kind1Event = await user.signer.signEvent({
          kind: 1,
          content: kind1Content,
          tags: kind1Tags,
          created_at: Math.floor(Date.now() / 1000),
        });
        nostr.event(kind1Event, { signal: AbortSignal.timeout(8000) }).catch(() => {});
      } catch {
        // Non-fatal
      }

      setYtPublishDone(true);
    } catch (err) {
      setYtPublishError(String(err));
    } finally {
      setYtPublishing(false);
    }
  }, [user, ytUrl, ytTitle, ytDescription, ytTagsInput, publishEvent, recordUpload, nostr, toast]);

  /* ── Publish to Nostr (called from Step 3: servers) ── */
  const handlePublish = () => {
    if (!title.trim()) {
      toast({ title: 'Title required', description: 'Add a title before publishing.', variant: 'destructive' });
      return;
    }

    // Re-entrancy guard — bail if a publish is already in-flight
    if (publishingRef.current) return;
    publishingRef.current = true;

    // Advance to step 4 immediately so the user sees the publishing spinner
    setStep('publishing');
    setPublishError('');
    setPublishDone(false);

    const tags: string[][] = [
      // Reuse the same d-tag across retries so a retry replaces the same
      // addressable event instead of creating a duplicate.
      ['d', eventDTagRef.current],
      ['title', title.trim()],
      ['published_at', String(Math.floor(Date.now() / 1000))],
      ['alt', title.trim()],
    ];

    // Detect MIME type from file or default to mp4
    const mime = video.file?.type ?? 'video/mp4';
    const imetaParts: string[] = [`url ${video.videoUrl}`, `m ${mime}`];
    if (thumb.url) imetaParts.push(`image ${thumb.url}`);
    if (duration) imetaParts.push(`duration ${duration}`);
    tags.push(['imeta', ...imetaParts]);

    tagsInput.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
      .forEach(t => tags.push(['t', t]));

    const footer = '\n\nhttps://goblinbox.net';
    const content = description.trim() ? description.trim() + footer : footer.trim();

    publishEvent(
      { kind: 34235, content, tags },
      {
        onSuccess: async (videoEvent: NostrEvent) => {
          // Record this upload in analytics
          recordUpload({
            pubkey: user.pubkey,
            eventId: videoEvent.id,
            title: title.trim(),
            timestamp: Math.floor(Date.now() / 1000),
            tags: tagsInput.split(',').map(t => t.trim().toLowerCase()).filter(Boolean),
          });

          // ── Companion kind 1 so the video appears in general social feeds ──
          try {
            const noteAddr = nip19.noteEncode(videoEvent.id);
            const parsedTags = tagsInput.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);

            // Build the kind 1 content:
            //   video URL  ← clients render this as an inline video player
            //   Title
            //   description (if any)
            //   hashtags (space-separated #tag list)
            //   https://goblinbox.net  ← footer
            const hashtagLine = parsedTags.map(t => `#${t}`).join(' ');
            const parts: string[] = [video.videoUrl, title.trim()];
            if (description.trim()) parts.push(description.trim());
            if (hashtagLine) parts.push(hashtagLine);
            parts.push('https://goblinbox.net');
            const kind1Content = parts.join('\n\n');

            // imeta tag so clients render a rich video/thumbnail preview (NIP-92)
            const kind1Mime = videoEvent.tags.find(([k]) => k === 'imeta')?.[1]?.match(/^m (.+)$/m)?.[1]
              ?? (video.file?.type ?? 'video/mp4');
            const kind1ImetaParts: string[] = [`url ${video.videoUrl}`, `m ${kind1Mime}`];
            if (thumb.url) kind1ImetaParts.push(`image ${thumb.url}`);
            if (duration) kind1ImetaParts.push(`duration ${duration}`);

            const kind1Tags: string[][] = [
              ['r', video.videoUrl],
              ['imeta', ...kind1ImetaParts],
              ['e', videoEvent.id, '', 'mention'],
            ];
            parsedTags.forEach(t => kind1Tags.push(['t', t]));

            // noteAddr is used in kind1Content above; suppress unused-variable lint
            void noteAddr;

            const kind1Event = await user.signer.signEvent({
              kind: 1,
              content: kind1Content,
              tags: kind1Tags,
              created_at: Math.floor(Date.now() / 1000),
            });
            // Fire-and-forget
            nostr.event(kind1Event, { signal: AbortSignal.timeout(8000) }).catch(() => {});
          } catch {
            // Non-fatal — the video event is already published
          }

          setPublishDone(true);
          // Guard stays true — modal will be closed/reset, no retry possible
        },
        onError: err => {
          // Release the guard so the user can retry
          publishingRef.current = false;
          setPublishError(String(err));
        },
      },
    );
  };

  if (!user) return null;

  // Upload access check
  const uploadAllowed = canUpload(user.pubkey, adminConfig);
  if (!uploadAllowed) {
    const blocked = (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-4">
        <div className="rounded-full p-4" style={{ background: 'rgba(244,67,54,0.12)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="#f44336">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
        </div>
        <p className="font-semibold" style={{ color: '#f44336' }}>Upload Restricted</p>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Your account is not permitted to upload content to Goblinbox at this time.
        </p>
        <button
          onClick={onClose}
          className="mt-2 px-4 py-2 rounded-lg text-sm"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
        >
          Close
        </button>
      </div>
    );
    if (isMobile) {
      return (
        <Drawer open={open} onOpenChange={v => !v && onClose()} dismissible>
          <DrawerContent
            className="flex flex-col"
            overlayClassName={MOBILE_DRAWER_OVERLAY_CLASS}
            style={mobileDrawerContentStyle}
          >
            <DrawerHeader style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--gb-border)' }}>
              <DrawerClose asChild>
                <button className="absolute right-4 top-4 rounded-full p-1" style={{ color: 'var(--text-secondary)' }} aria-label="Close">
                  <X size={18} />
                </button>
              </DrawerClose>
              <DrawerTitle style={{ color: 'var(--text-primary)' }}>Upload Video</DrawerTitle>
            </DrawerHeader>
            <div style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>{blocked}</div>
          </DrawerContent>
        </Drawer>
      );
    }
    return (
      <Dialog open={open} onOpenChange={v => !v && onClose()}>
        <DialogContent style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>Upload Video</DialogTitle>
          </DialogHeader>
          {blocked}
        </DialogContent>
      </Dialog>
    );
  }

  /* YouTube video preview for the bridge form */
  const ytVideoId = (() => {
    try { return extractYouTubeVideoId(ytUrl.trim()); } catch { return null; }
  })();

  /* ── Shared body (steps) ── */
  const modalBody = (
    <>
      {/* ── Mode switcher ── */}
      <div
        className="flex rounded-lg p-0.5 mb-5"
        style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)' }}
      >
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all',
          )}
          style={
            mode === 'upload'
              ? { background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }
              : { color: 'var(--text-secondary)' }
          }
        >
          <Upload size={14} />
          Upload File
        </button>
        <button
          type="button"
          onClick={() => setMode('youtube')}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all',
          )}
          style={
            mode === 'youtube'
              ? { background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }
              : { color: 'var(--text-secondary)' }
          }
        >
          <YouTubeIcon size={14} />
          YouTube Bridge
        </button>
      </div>

      {/* ══ YOUTUBE BRIDGE MODE ══════════════════════════════════════════ */}
      {mode === 'youtube' && (
        <div className="space-y-4">
          {ytPublishDone ? (
            /* Success */
            <div className="flex flex-col items-center justify-center py-10 gap-6 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(58,158,58,0.15)' }}>
                <Check size={32} style={{ color: 'var(--gb-accent)' }} />
              </div>
              <div>
                <p className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>Video published! 🎉</p>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Your YouTube video is now bridged to Nostr.</p>
              </div>
              <button type="button" onClick={onClose}
                className="px-6 py-2.5 rounded-lg font-medium text-sm text-white transition-all hover:opacity-90"
                style={{ background: 'var(--gb-accent)' }}>
                Done
              </button>
            </div>
          ) : ytPublishing ? (
            /* Publishing spinner */
            <div className="flex flex-col items-center justify-center py-10 gap-6 text-center">
              <div className="w-14 h-14 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: 'var(--gb-accent)', borderTopColor: 'transparent' }} />
              <div>
                <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>Publishing to Nostr…</p>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Signing and broadcasting your video event.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Explanation banner */}
              <div
                className="rounded-xl p-3 flex items-start gap-3"
                style={{ background: 'rgba(255,0,0,0.07)', border: '1px solid rgba(255,0,0,0.2)' }}
              >
                <YouTubeIcon size={18} className="shrink-0 mt-0.5" style={{ color: '#ff0000' } as React.CSSProperties} />
                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  Paste a YouTube URL to publish it as a Nostr video event. The video will appear
                  in Goblinbox as an embedded YouTube player—no file upload needed.
                </p>
              </div>

              {/* YouTube URL input */}
              <div className="space-y-1.5">
                <Label style={{ color: 'var(--text-primary)' }}>YouTube URL <span style={{ color: '#f44336' }}>*</span></Label>
                <Input
                  value={ytUrl}
                  onChange={e => { setYtUrl(e.target.value); setYtUrlError(''); }}
                  placeholder="https://www.youtube.com/watch?v=…"
                  style={{
                    background: 'var(--bg-primary)',
                    border: `1px solid ${ytUrlError ? '#f44336' : isYouTubeUrl(ytUrl.trim()) ? 'var(--gb-accent)' : 'var(--gb-border)'}`,
                    color: 'var(--text-primary)',
                  }}
                />
                {ytUrlError && <p className="text-xs" style={{ color: '#f44336' }}>{ytUrlError}</p>}
                {isYouTubeUrl(ytUrl.trim()) && (
                  <p className="text-xs flex items-center gap-1" style={{ color: 'var(--gb-accent)' }}>
                    <Check size={12} /> Valid YouTube URL detected
                  </p>
                )}
              </div>

              {/* Live preview */}
              {ytVideoId && (
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--gb-border)' }}>
                  <YouTubeEmbed videoId={ytVideoId} originalUrl={ytUrl} fillParent />
                </div>
              )}

              {/* Title */}
              <div className="space-y-1.5">
                <Label style={{ color: 'var(--text-primary)' }}>Title <span style={{ color: '#f44336' }}>*</span></Label>
                <Input
                  value={ytTitle}
                  onChange={e => setYtTitle(e.target.value)}
                  placeholder="Give this video a title"
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label style={{ color: 'var(--text-primary)' }}>Description</Label>
                <Textarea
                  value={ytDescription}
                  onChange={e => setYtDescription(e.target.value)}
                  placeholder="Tell viewers about this video…"
                  rows={3}
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)', resize: 'none' }}
                />
              </div>

              {/* Tags */}
              <div className="space-y-1.5">
                <Label style={{ color: 'var(--text-primary)' }}>Tags</Label>
                <Input
                  value={ytTagsInput}
                  onChange={e => setYtTagsInput(e.target.value)}
                  placeholder="bitcoin, nostr, gaming"
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
                />
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Comma-separated. A <strong>youtube</strong> tag is added automatically.
                </p>
              </div>

              {/* Publish error */}
              {ytPublishError && (
                <div className="rounded-xl p-3 flex items-start gap-3" style={{ background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)' }}>
                  <AlertCircle size={16} color="#f44336" className="shrink-0 mt-0.5" />
                  <p className="text-xs" style={{ color: '#f44336' }}>{ytPublishError}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2 rounded-lg font-medium text-sm transition-colors"
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!ytVideoId || !ytTitle.trim() || ytPublishing}
                  onClick={handleYouTubePublish}
                  className="flex-1 py-2 rounded-lg font-medium text-sm text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: '#ff0000' }}
                >
                  <YouTubeIcon size={14} />
                  Bridge to Nostr
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ UPLOAD MODE (steps) ═══════════════════════════════════════ */}
      {mode === 'upload' && (
        <>
      <StepIndicator step={step} />

      {/* ══ STEP 1: file ══════════════════════════════════════════════ */}
      {step === 'file' && (
        <div className="space-y-4">
          {!video.uploading && !video.uploadError && !video.file && (
            <>
              {/* Server picker collapsed under toggle */}
              <button
                type="button"
                onClick={() => setShowServersInline(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors"
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-secondary)' }}
              >
                <span className="flex items-center gap-2">
                  <Server size={14} />
                  <span>{selectedServers.length} server{selectedServers.length !== 1 ? 's' : ''} selected</span>
                  <span className="flex gap-1">
                    {selectedServers.slice(0, 3).map(s => (
                      <span key={s} className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(58,158,58,0.15)', color: 'var(--gb-accent)' }}>
                        {new URL(s).hostname.replace('blossom.', '')}
                      </span>
                    ))}
                  </span>
                </span>
                {showServersInline ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>

              {showServersInline && (
                <div className="rounded-xl p-3 space-y-3" style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)' }}>
                  <BlossomServerManager selectedServers={selectedServers} onChange={setSelectedServers} />
                </div>
              )}

              <FileDropZone
                onFile={handleVideoFile}
                accept="video/*"
                label="Click or drag a video file here"
                hint="MP4, WebM, MOV · No size limit"
              />

              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs transition-opacity hover:opacity-70"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          {/* Uploading progress */}
          {video.uploading && video.file && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <div className="rounded-xl p-2.5" style={{ background: 'rgba(58,158,58,0.12)' }}>
                  <Upload size={20} style={{ color: 'var(--gb-accent)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{video.file.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{formatBytes(video.file.size)}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <span>Uploading to Blossom…</span>
                  <span>{video.uploadProgress}%</span>
                </div>
                <Progress value={video.uploadProgress} className="h-2" />
                <div className="flex flex-wrap gap-1">
                  {selectedServers.map(s => (
                    <span key={s} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(58,158,58,0.1)', color: 'var(--gb-accent)' }}>
                      {new URL(s).hostname}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Upload error */}
          {video.uploadError && (
            <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)' }}>
              <AlertCircle size={18} color="#f44336" className="shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: '#f44336' }}>Upload failed</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{video.uploadError}</p>
              </div>
              <button
                type="button"
                onClick={() => setVideo(v => ({ ...v, uploadError: '', file: null }))}
                className="shrink-0"
                style={{ color: 'var(--text-secondary)' }}
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══ STEP 2: details ═══════════════════════════════════════════ */}
      {step === 'details' && (
        <div className="space-y-4">
          {/* Success banner */}
          <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(58,158,58,0.1)', border: '1px solid rgba(58,158,58,0.3)' }}>
            <div className="rounded-full p-1" style={{ background: 'var(--gb-accent)' }}>
              <Check size={12} color="#fff" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold" style={{ color: 'var(--gb-accent)' }}>Video uploaded successfully</p>
              <p className="text-xs truncate mt-0.5 font-mono" style={{ color: 'var(--text-secondary)' }}>{video.videoUrl}</p>
            </div>
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label style={{ color: 'var(--text-primary)' }}>Title <span style={{ color: '#f44336' }}>*</span></Label>
            <Input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Give your video a title"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label style={{ color: 'var(--text-primary)' }}>Description</Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Tell viewers about this video…"
              rows={3}
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)', resize: 'none' }}
            />
          </div>

          {/* Tags */}
          <div className="space-y-1.5">
            <Label style={{ color: 'var(--text-primary)' }}>Tags</Label>
            <Input
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              placeholder="bitcoin, nostr, gaming"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
            />
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Comma-separated. Helps people find your video.</p>
          </div>

          {/* Duration */}
          <div className="space-y-1.5">
            <Label style={{ color: 'var(--text-primary)' }}>Duration (seconds)</Label>
            <Input
              type="number"
              value={duration}
              onChange={e => setDuration(e.target.value)}
              placeholder="e.g. 300"
              min="0"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
            />
          </div>

          {/* Thumbnail */}
          <div className="space-y-1.5">
            <Label style={{ color: 'var(--text-primary)' }}>Thumbnail</Label>
            {thumb.url ? (
              <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
                <img src={thumb.url} alt="thumbnail" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setThumb(t => ({ ...t, file: null, url: '' }))}
                  className="absolute top-2 right-2 rounded-full p-1.5"
                  style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (thumb.uploading || autoThumbGenerating) ? (
              <div className="rounded-xl flex items-center justify-center py-6" style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)' }}>
                <div className="text-center space-y-2">
                  <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin mx-auto" style={{ borderColor: 'var(--gb-accent)', borderTopColor: 'transparent' }} />
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {autoThumbGenerating && !thumb.uploading ? 'Capturing frame…' : 'Uploading thumbnail…'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {video.videoUrl && (
                  <button
                    type="button"
                    onClick={handleAutoThumbnail}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all hover:opacity-90"
                    style={{
                      background: 'rgba(58,158,58,0.12)',
                      border: '1px solid rgba(58,158,58,0.4)',
                      color: 'var(--gb-accent)',
                    }}
                  >
                    <Sparkles size={16} />
                    Auto-generate from video
                  </button>
                )}
                <FileDropZone
                  onFile={handleThumbFile}
                  accept="image/*"
                  label="Click or drag an image"
                  hint="JPG, PNG, WebP · or use auto-generate above"
                />
              </div>
            )}
            {thumb.error && <p className="text-xs" style={{ color: '#f44336' }}>{thumb.error}</p>}
          </div>

          {/* Navigation: back to file, forward to servers */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setStep('file')}
              className="flex-1 py-2 rounded-lg font-medium text-sm transition-colors"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
            >
              ← Back
            </button>
            <button
              type="button"
              disabled={!title.trim()}
              onClick={() => setStep('servers')}
              className="flex-1 py-2 rounded-lg font-medium text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: 'var(--gb-accent)' }}
            >
              Next: Servers →
            </button>
          </div>
        </div>
      )}

      {/* ══ STEP 3: servers ═══════════════════════════════════════════ */}
      {step === 'servers' && (
        <div className="space-y-4">
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Review the Blossom servers your video was uploaded to, or add extras for redundancy.
          </p>
          <BlossomServerManager selectedServers={selectedServers} onChange={setSelectedServers} />

          {/* Navigation: back to details, forward triggers publish → step 4 */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setStep('details')}
              className="flex-1 py-2 rounded-lg font-medium text-sm transition-colors"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
            >
              ← Back
            </button>
            <button
              type="button"
              disabled={isPublishing}
              onClick={handlePublish}
              className="flex-1 py-2 rounded-lg font-medium text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: 'var(--gb-accent)' }}
            >
              Publish to Nostr →
            </button>
          </div>
        </div>
      )}

      {/* ══ STEP 4: publishing ════════════════════════════════════════ */}
      {step === 'publishing' && (
        <div className="flex flex-col items-center justify-center py-10 gap-6 text-center">
          {!publishDone && !publishError && (
            <>
              <div className="w-14 h-14 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: 'var(--gb-accent)', borderTopColor: 'transparent' }} />
              <div>
                <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>Publishing to Nostr…</p>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Signing and broadcasting your video event.</p>
              </div>
            </>
          )}

          {publishDone && !publishError && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(58,158,58,0.15)' }}>
                <Check size={32} style={{ color: 'var(--gb-accent)' }} />
              </div>
              <div>
                <p className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>Video published! 🎉</p>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Your video is now live on Nostr.</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 rounded-lg font-medium text-sm text-white transition-all hover:opacity-90"
                style={{ background: 'var(--gb-accent)' }}
              >
                Done
              </button>
            </>
          )}

          {publishError && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(244,67,54,0.12)' }}>
                <AlertCircle size={32} color="#f44336" />
              </div>
              <div>
                <p className="font-semibold" style={{ color: '#f44336' }}>Publish failed</p>
                <p className="text-sm mt-1 max-w-xs" style={{ color: 'var(--text-secondary)' }}>{publishError}</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setStep('servers'); setPublishError(''); }}
                  className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  disabled={isPublishing}
                  onClick={handlePublish}
                  className="px-4 py-2 rounded-lg font-medium text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ background: 'var(--gb-accent)' }}
                >
                  Retry
                </button>
              </div>
            </>
          )}
        </div>
      )}
        </>
      )}
    </>
  );

  /* ── Mobile: Drawer ── */
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={v => !v && onClose()} dismissible>
        <DrawerContent
          className="flex flex-col"
          overlayClassName={MOBILE_DRAWER_OVERLAY_CLASS}
          style={mobileDrawerContentStyle}
        >
          <DrawerHeader
            className="shrink-0 relative pb-2"
            style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--gb-border)' }}
          >
            <DrawerClose asChild>
              <button
                className="absolute right-4 top-4 rounded-full p-1"
                style={{ color: 'var(--text-secondary)' }}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </DrawerClose>
            <DrawerTitle
              className="flex items-center gap-2 pt-1"
              style={{ color: 'var(--text-primary)' }}
            >
              <Upload size={16} style={{ color: 'var(--gb-accent)' }} />
              Upload Video
            </DrawerTitle>
          </DrawerHeader>
          <div
            className="flex-1 overflow-y-auto p-4"
            style={{
              background: 'var(--bg-secondary)',
              paddingBottom: '1.5rem',
              color: 'var(--text-primary)',
            }}
          >
            {modalBody}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  /* ── Desktop: Dialog ── */
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent
        className="max-w-lg w-full max-h-[92dvh] flex flex-col overflow-hidden"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Upload size={18} style={{ color: 'var(--gb-accent)' }} />
            Upload Video
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          {modalBody}
        </div>
      </DialogContent>
    </Dialog>
  );
}
